<template>
    <Loading v-if="loading"/>
    <Page>
        <Container>
            <div class="mx-auto max-w-sm">

                <div class="flex items-center justify-between mb-6">
                    <Title>{{pageTitle}}</Title>
                    <div class="cursor-pointer" @click="handleCancel">
                        <Icon icon="times" class="text-2xl"/>
                    </div>
                </div>

                <Field>
                    <Label>Name *</Label>
                    <TextInput v-model="form.fields.name" placeholder="Name"/>
                    <FormError :form="form" field="name"/>
                </Field>

                <Field>
                    <Label>Template Items</Label>

                    <draggable v-model="form.fields.items"
                               handle=".drag-handle"
                               @start="drag=true"
                               @end="drag=false"
                               item-key="key">
                        <template #item="{element, index}">
                            <div v-if="isCircuitFirstItem(index)"
                                 class="relative z-0 my-3 rounded border border-brand-primary-500/40 bg-neutral-950 focus-within:z-20">
                                <div class="border-b border-brand-primary-500/30 bg-brand-primary-500/10 px-3 py-3">
                                    <div class="flex items-center justify-between gap-3">
                                        <div class="min-w-0 truncate font-semibold text-brand-primary-300">
                                            {{ element.circuitLabel || 'Circuit' }}
                                        </div>
                                        <div class="shrink-0 text-xs font-semibold text-neutral-300">
                                            {{ element.circuitRounds }} rounds
                                        </div>
                                    </div>
                                    <div class="mt-3 grid grid-cols-2 gap-2">
                                        <Dropdown>
                                            <template v-slot:toggle>
                                                <button type="button"
                                                        class="w-full rounded border border-neutral-600 px-2 py-2 text-xs">
                                                    + Movement
                                                </button>
                                            </template>
                                            <template v-slot:dropdown>
                                                <div class="max-h-56 w-56 overflow-auto scroll-bar-hidden">
                                                    <template v-for="movement in movements">
                                                        <div class="cursor-pointer px-4 py-2"
                                                             @click="handleCircuitMovementAdd(element.circuitId, movement)">
                                                            {{ movement.name }}
                                                        </div>
                                                    </template>
                                                </div>
                                            </template>
                                        </Dropdown>
                                        <button type="button"
                                                class="rounded border border-neutral-600 px-2 py-2 text-xs"
                                                @click="handleCircuitRestAdd(element.circuitId)">
                                            + Rest After
                                        </button>
                                        <button type="button"
                                                class="rounded border border-neutral-600 px-2 py-2 text-xs"
                                                @click="handleCircuitRoundRemove(element.circuitId)">
                                            − Round
                                        </button>
                                        <button type="button"
                                                class="rounded border border-neutral-600 px-2 py-2 text-xs"
                                                @click="handleCircuitRoundAdd(element.circuitId)">
                                            + Round
                                        </button>
                                        <button type="button"
                                                class="col-span-2 rounded border border-red-800 px-2 py-2 text-xs font-semibold text-red-400"
                                                @click="handleCircuitRemove(element.circuitId)">
                                            Remove Circuit
                                        </button>
                                    </div>
                                </div>
                                <draggable :model-value="circuitPreviewItems(element.circuitId)"
                                           class="divide-y divide-neutral-800"
                                           handle=".circuit-drag-handle"
                                           item-key="key"
                                           @update:modelValue="items => handleCircuitItemsSort(element.circuitId, items)">
                                    <template #item="{element: circuitItem, index: circuitIndex}">
                                    <div class="flex min-h-12 items-center gap-3 px-3 py-3">
                                        <div class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-neutral-800 text-xs font-semibold text-neutral-300">
                                            {{ circuitIndex + 1 }}
                                        </div>
                                        <template v-if="circuitItem.type === 'movement'">
                                            <div class="min-w-0 flex-1 truncate font-semibold leading-snug"
                                                 :title="movementName(circuitItem)">
                                                {{ movementName(circuitItem) }}
                                            </div>
                                            <Icon class="circuit-drag-handle shrink-0 cursor-pointer text-neutral-400"
                                                  icon="sort"/>
                                            <button type="button"
                                                    class="shrink-0 rounded border border-neutral-700 px-2 py-1 text-xs text-neutral-300"
                                                    @click="handleCircuitStepRestAdd(element.circuitId, circuitItem)">
                                                + Rest
                                            </button>
                                        </template>
                                        <template v-else>
                                            <Icon class="shrink-0 text-neutral-500" fixedWidth icon="stopwatch"/>
                                            <div class="min-w-0 flex-1 truncate text-sm text-neutral-400">
                                                {{ circuitItem.label || 'Rest Timer' }}
                                            </div>
                                            <div class="shrink-0 text-sm text-neutral-400">
                                                {{ circuitItem.duration }} sec
                                            </div>
                                            <button type="button"
                                                    class="flex h-7 w-7 shrink-0 items-center justify-center rounded border border-neutral-700 text-neutral-400"
                                                    aria-label="Remove rest from every round"
                                                    title="Remove rest from every round"
                                                    @click="handleCircuitStepRemove(element.circuitId, circuitItem)">
                                                <Icon icon="times"/>
                                            </button>
                                        </template>
                                    </div>
                                    </template>
                                </draggable>
                            </div>
                            <div v-else-if="isInsideCircuit(index)" class="hidden"></div>
                            <div v-else-if="element.type === 'rest_timer'"
                                 class="my-2 flex items-center justify-between gap-3 rounded border border-neutral-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-950">
                                <div class="min-w-0 flex-1">
                                    <div class="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
                                        <div class="min-w-0 flex-1 font-semibold">{{ element.label || 'Rest Timer' }}</div>
                                        <div class="flex flex-none items-center gap-2">
                                            <div class="w-20 flex-none">
                                                <NumberInput class="!w-20 !flex-none text-center" :min="1" :max="7200" v-model="element.duration"/>
                                            </div>
                                            <div class="text-sm text-neutral-600 dark:text-neutral-400">sec</div>
                                        </div>
                                    </div>
                                </div>
                                <div class="flex items-center justify-between gap-x-4">
                                    <Icon class="cursor-pointer drag-handle" icon="sort"/>
                                    <Icon class="cursor-pointer" icon="times" @click="handleItemRemove(index)"/>
                                </div>
                            </div>
                            <div v-else :class="exerciseSetsEnabled ? movementItemClasses(index) : 'my-2 flex items-center justify-between gap-3 rounded border border-neutral-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-950'">
                                <template v-if="exerciseSetsEnabled">
                                    <div class="flex items-center justify-between gap-3 border-b border-neutral-200 px-3 py-2 dark:border-neutral-700"
                                         v-if="['single', 'first', 'only'].includes(setGroupPosition(index))">
                                        <div class="min-w-0 font-semibold">
                                            {{ movementName(element) }}
                                        </div>
                                        <button type="button"
                                                class="flex shrink-0 items-center justify-center gap-1 rounded border border-neutral-500 px-3 py-2 text-sm"
                                                @click="handleSetAdd(setGroupLastItemKey(element) || element.key)">
                                            <Icon fixedWidth icon="plus"/>
                                            Set
                                        </button>
                                    </div>
                                    <div class="flex items-stretch bg-neutral-50 dark:bg-white/[2%]">
                                        <div class="flex w-16 shrink-0 items-center justify-center border-r border-neutral-200 text-xs font-semibold text-neutral-600 dark:border-neutral-700 dark:text-neutral-400">
                                            Set {{ setNumber(element) }}
                                        </div>
                                        <div class="flex min-w-0 flex-1 items-center justify-between gap-3 px-3 py-2">
                                            <div class="min-w-0 truncate">
                                                {{ movementName(element) }}
                                            </div>
                                            <div class="flex shrink-0 items-center justify-between gap-x-4">
                                                <Icon class="cursor-pointer drag-handle" icon="sort"/>
                                                <Icon class="cursor-pointer" icon="times" @click="handleItemRemove(index)"/>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                                <template v-else>
                                    <div class="min-w-0 flex-1">
                                        {{ movementName(element) }}
                                    </div>
                                    <div class="flex items-center justify-between gap-x-4">
                                        <Icon class="cursor-pointer drag-handle" icon="sort"/>
                                        <Icon class="cursor-pointer" icon="times" @click="handleItemRemove(index)"/>
                                    </div>
                                </template>
                            </div>
                        </template>
                    </draggable>

                    <div class="grid grid-cols-1 gap-3">
                    <Dropdown>
                        <template v-slot:toggle>
                            <Button block primary outlined>
                                Add Movement
                            </Button>
                        </template>
                        <template v-slot:dropdown>
                            <div class="max-h-56 w-full overflow-auto scroll-bar-hidden">
                                <template v-for="movement in movements">
                                    <div class="px-4 py-2 cursor-pointer" @click="handleMovementAdd(movement)">
                                        {{ movement.name }}
                                    </div>
                                </template>
                            </div>
                        </template>
                    </Dropdown>

                    <Dropdown>
                        <template v-slot:toggle>
                            <Button block secondary outlined>
                                <Icon fixedWidth icon="repeat"/>
                                Add Circuit
                            </Button>
                        </template>
                        <template v-slot:dropdown>
                            <div class="max-h-56 w-full overflow-auto scroll-bar-hidden">
                                <template v-for="movement in movements">
                                    <div class="px-4 py-2 cursor-pointer" @click="handleCircuitAdd(movement)">
                                        Start with {{ movement.name }}
                                    </div>
                                </template>
                            </div>
                        </template>
                    </Dropdown>

                    <Button block secondary outlined @click="handleRestTimerAdd">
                        <Icon fixedWidth icon="stopwatch"/>
                        Add Rest Timer
                    </Button>
                    </div>
                </Field>

                <MarketplaceListingPanel v-if="template"
                                         :resource="template"
                                         resourceType="template"
                />

                <Controls>

                    <template v-slot:left>
                        <Button danger outlined @click="handleDelete" v-if="template">
                            Delete
                        </Button>
                    </template>

                    <template v-slot:right>
                        <div class="flex items-center justify-between gap-4">
                            <Button primary @click="handleSave">
                                Save
                            </Button>
                        </div>
                    </template>

                </Controls>

            </div>
        </Container>
    </Page>
</template>

<script>
import {defineComponent} from 'vue'
import {mapActions} from 'vuex';
import {Form} from '@/internal.js';
import draggable from 'vuedraggable'
import {capturePostHogEvent} from '@/utils/posthog';
import NumberInput from '@/components/NumberInput.vue';
import MarketplaceListingPanel from '@/components/Marketplace/ListingPanel.vue';
import {uuid} from '@/helpers.js';
import {settingIsEnabled} from '@/utils/settings.js';

export default defineComponent({
  name: 'Form',
  components: {
    MarketplaceListingPanel,
    draggable,
    NumberInput,
  },
  computed: {
    movements() {
      return this.$store.getters['movement/all'].sort((a, b) => a.name > b.name ? 1 : -1) || [];
    },
    pageTitle() {
      return this.template ? 'Edit Template' : 'New Template';
    },
    template() {
      return this.$route.params.templateUUID ? this.$store.getters['template/findBy'](this.$route.params.templateUUID, 'uuid') : null;
    },
    exerciseSetsEnabled() {
      return settingIsEnabled(this.user?.settings, 'exerciseSetsEnabled', 'exercise_sets_enabled', true);
    },
    user() {
      return this.$store.getters['auth/user'];
    },
  },
  async created() {

    await this.loadMovements();

    if (this.$route.params.templateUUID) {
      this.loadTemplate();
    }
  },
  data() {
    return {
      drag: false,
      form: new Form({
        name: '',
        items: [],
      }),
      loading: false,
    };
  },
  methods: {
    ...mapActions({
      movementIndex: 'movement/index',
      templateDestroy: 'template/destroy',
      templateShow: 'template/show',
      templateStore: 'template/store',
      templateUpdate: 'template/update',
    }),
    fillForm() {
      this.form.fields = {
        name: this.template.name,
        items: this.templateItemsFromTemplate(this.template),
      };
    },
    handleCancel() {
      this.$router.push(window.app.findRouteByName('template/index'));
    },
    handleDelete() {
      window.app.dialog({
        title: 'Delete Template',
        message: 'Are you sure you want to delete this template?',
        actions: [
          {
            text: 'No',
            outlined: true,
            click: () => {
            },
          },
          {
            text: 'Yes',
            danger: true,
            click: () => {

              this.loading = true;
              this.templateDestroy({
                id: this.template.id,
              }).then(() => {
                window.app.snackbar('Template Deleted');
                this.selectedFile = null;
                this.$router.push(window.app.findRouteByName('template/index'));
              }).finally(() => {
                this.loading = false;
              });

            },
          }
        ],
      });
    },
    handleMovementAdd(movement) {
      this.form.fields.items = [
        ...this.form.fields.items,
        {
          key: uuid(),
          type: 'movement',
          movement,
          movementId: movement.id,
          setId: null,
        },
      ];
    },
    handleCircuitAdd(movement) {
      const circuitId = uuid();
      const circuitLabel = `${movement.name} Circuit`;
      const circuitRounds = 3;
      const items = [];

      for (let circuitRound = 1; circuitRound <= circuitRounds; circuitRound += 1) {
        items.push(this.newCircuitMovement({
          circuitId,
          circuitLabel,
          circuitRound,
          circuitRounds,
          movement,
        }));
      }

      this.form.fields.items = [...this.form.fields.items, ...items];
      capturePostHogEvent('template_circuit_added', {
        movement_id: movement.id,
        round_count: circuitRounds,
      });
    },
    handleCircuitMovementAdd(circuitId, movement) {
      const circuitItems = this.circuitItems(circuitId);

      if (!circuitItems.length) {
        return;
      }

      const circuitLabel = circuitItems[0].circuitLabel || 'Circuit';
      const circuitRounds = Number(circuitItems[0].circuitRounds || 1);
      const updatedItems = [...this.form.fields.items];

      for (let circuitRound = circuitRounds; circuitRound >= 1; circuitRound -= 1) {
        const lastIndex = updatedItems.findLastIndex(item => {
          return item.type === 'movement'
            && item.circuitId === circuitId
            && Number(item.circuitRound) === circuitRound;
        });

        updatedItems.splice(lastIndex + 1, 0, this.newCircuitMovement({
          circuitId,
          circuitLabel,
          circuitRound,
          circuitRounds,
          movement,
        }));
      }

      this.form.fields.items = updatedItems;
    },
    handleCircuitRestAdd(circuitId) {
      const lastIndex = this.form.fields.items.findLastIndex(item => {
        return item.type === 'movement' && item.circuitId === circuitId;
      });

      if (lastIndex < 0) {
        return;
      }

      const circuitLabel = this.form.fields.items[lastIndex].circuitLabel || 'Circuit';
      this.form.fields.items.splice(lastIndex + 1, 0, {
        key: uuid(),
        type: 'rest_timer',
        duration: this.$store.getters['auth/user']?.settings?.restTimerDuration || 90,
        label: `${circuitLabel} complete`,
        afterCircuitId: circuitId,
      });
    },
    handleCircuitStepRestAdd(circuitId, previewMovement) {
      const circuitItems = this.circuitItems(circuitId);

      if (!circuitItems.length) {
        return;
      }

      const previewRoundMovements = circuitItems.filter(item => Number(item.circuitRound) === 1);
      const movementPosition = previewRoundMovements.findIndex(item => item.key === previewMovement.key);

      if (movementPosition < 0) {
        return;
      }

      const circuitLabel = circuitItems[0].circuitLabel || 'Circuit';
      const circuitRounds = Number(circuitItems[0].circuitRounds || 1);
      const circuitStepId = uuid();
      const updatedItems = [...this.form.fields.items];

      for (let circuitRound = circuitRounds; circuitRound >= 1; circuitRound -= 1) {
        const roundMovements = updatedItems.filter(item => {
          return item.type === 'movement'
            && item.circuitId === circuitId
            && Number(item.circuitRound) === circuitRound;
        });
        const targetMovement = roundMovements[movementPosition];
        const targetIndex = updatedItems.findIndex(item => item.key === targetMovement?.key);

        if (targetIndex < 0) {
          continue;
        }

        updatedItems.splice(targetIndex + 1, 0, {
          key: uuid(),
          type: 'rest_timer',
          circuitId,
          circuitLabel,
          circuitRound,
          circuitRounds,
          circuitStepId,
          duration: 30,
          label: 'Circuit rest',
        });
      }

      this.form.fields.items = updatedItems;
      capturePostHogEvent('template_circuit_rest_added', {
        circuit_rounds: circuitRounds,
        movement_id: previewMovement.movementId || previewMovement.movement?.id || null,
      });
    },
    handleCircuitItemsSort(circuitId, sortedPreviewItems) {
      const sortedMovements = sortedPreviewItems
        .filter(item => item.type === 'movement')
        .map(item => ({
          movement: item.movement,
          movementId: item.movementId || item.movement?.id,
          setId: item.setId || item.set_id || null,
        }));
      const movementPositionByKey = new Map();

      this.form.fields.items.forEach(item => {
        if (item.type !== 'movement' || item.circuitId !== circuitId) {
          return;
        }

        const circuitRound = Number(item.circuitRound);
        const movementPosition = movementPositionByKey.get(circuitRound) || 0;
        movementPositionByKey.set(circuitRound, movementPosition + 1);

        const sortedMovement = sortedMovements[movementPosition];

        if (!sortedMovement) {
          return;
        }

        item.movement = sortedMovement.movement;
        item.movementId = sortedMovement.movementId;
        item.setId = sortedMovement.setId;
      });
    },
    handleCircuitStepRemove(circuitId, circuitRest) {
      const circuitStepId = circuitRest.circuitStepId || circuitRest.circuit_step_id;

      if (circuitStepId) {
        this.form.fields.items = this.form.fields.items.filter(item => {
          return (item.circuitStepId || item.circuit_step_id) !== circuitStepId;
        });

        return;
      }

      const circuitRests = this.form.fields.items.filter(item => {
        return item.type === 'rest_timer' && item.circuitId === circuitId;
      });
      const previewRound = Number(circuitRest.circuitRound);
      const restPosition = circuitRests
        .filter(item => Number(item.circuitRound) === previewRound)
        .findIndex(item => item.key === circuitRest.key);

      if (restPosition < 0) {
        return;
      }

      const rounds = [...new Set(circuitRests.map(item => Number(item.circuitRound)))];
      const keysToRemove = new Set(rounds.map(round => {
        return circuitRests.filter(item => Number(item.circuitRound) === round)[restPosition]?.key;
      }).filter(Boolean));

      this.form.fields.items = this.form.fields.items.filter(item => {
        return !keysToRemove.has(item.key);
      });
    },
    handleCircuitRemove(circuitId) {
      this.form.fields.items = this.form.fields.items.filter(item => {
        return item.circuitId !== circuitId && item.afterCircuitId !== circuitId;
      });
    },
    handleCircuitRoundAdd(circuitId) {
      const circuitItems = this.circuitItems(circuitId);

      if (!circuitItems.length) {
        return;
      }

      const circuitRounds = Number(circuitItems[0].circuitRounds || 1);
      const lastRoundItems = this.form.fields.items.filter(item => {
        return item.circuitId === circuitId && Number(item.circuitRound) === circuitRounds;
      });
      const lastIndex = this.form.fields.items.findLastIndex(item => {
        return item.circuitId === circuitId;
      });
      const newRoundItems = lastRoundItems.map(item => ({
        ...item,
        key: uuid(),
        circuitRound: circuitRounds + 1,
        circuitRounds: circuitRounds + 1,
        setId: null,
      }));

      this.form.fields.items = this.form.fields.items.map(item => {
        return item.circuitId === circuitId
          ? {...item, circuitRounds: circuitRounds + 1}
          : item;
      });
      this.form.fields.items.splice(lastIndex + 1, 0, ...newRoundItems);
    },
    handleCircuitRoundRemove(circuitId) {
      const circuitItems = this.circuitItems(circuitId);

      if (!circuitItems.length) {
        return;
      }

      const circuitRounds = Number(circuitItems[0].circuitRounds || 1);

      if (circuitRounds <= 1) {
        return;
      }

      this.form.fields.items = this.form.fields.items
        .filter(item => {
          return !(item.circuitId === circuitId
            && Number(item.circuitRound) === circuitRounds);
        })
        .map(item => {
          return item.circuitId === circuitId
            ? {...item, circuitRounds: circuitRounds - 1}
            : item;
        });
    },
    handleItemRemove(index) {
      this.form.fields.items.splice(index, 1);
    },
    handleSetAdd(key) {
      if (!this.exerciseSetsEnabled) {
        return;
      }

      const itemIndex = this.form.fields.items.findIndex((item) => item.key === key && item.type === 'movement');

      if (itemIndex < 0) {
        return;
      }

      const item = this.form.fields.items[itemIndex];
      const setId = item.setId || item.set_id || uuid();
      const setCountBeforeAdd = item.setId || item.set_id ? this.setCount(item) : 1;
      const updatedItems = this.form.fields.items.map((localItem, index) => {
        if (index !== itemIndex) {
          return localItem;
        }

        return {
          ...localItem,
          setId,
        };
      });

      const newItem = {
        key: uuid(),
        type: 'movement',
        movement: item.movement,
        movementId: item.movementId || item.movement?.id,
        setId,
      };

      this.form.fields.items = [
        ...updatedItems.slice(0, itemIndex + 1),
        newItem,
        ...updatedItems.slice(itemIndex + 1),
      ];

      capturePostHogEvent('template_exercise_set_added', {
        movement_id: newItem.movementId || null,
        set_count_before_add: setCountBeforeAdd,
      });
    },
    handleRestTimerAdd() {
      const duration = this.$store.getters['auth/user']?.settings?.restTimerDuration || 90;

      this.form.fields.items = [
        ...this.form.fields.items,
        {
          key: uuid(),
          type: 'rest_timer',
          duration,
          label: 'Rest Timer',
        },
      ];

      capturePostHogEvent('template_rest_timer_added', {
        duration,
      });
    },
    handleSave() {

      if (this.loading) {
        return false;
      }

      let action;
      let payload = {
        ...this.form.fields,
        items: [
          ...this.form.fields.items.map((item) => {
            if (item.type === 'rest_timer') {
              return {
                type: 'rest_timer',
                circuit_id: item.circuitId || item.circuit_id || null,
                circuit_label: item.circuitLabel || item.circuit_label || null,
                circuit_round: item.circuitRound || item.circuit_round || null,
                circuit_rounds: item.circuitRounds || item.circuit_rounds || null,
                circuit_step_id: item.circuitStepId || item.circuit_step_id || null,
                after_circuit_id: item.afterCircuitId || item.after_circuit_id || null,
                duration: Number(item.duration || 90),
                label: item.label || null,
              };
            }

            return {
              type: 'movement',
              circuit_id: item.circuitId || item.circuit_id || null,
              circuit_label: item.circuitLabel || item.circuit_label || null,
              circuit_round: item.circuitRound || item.circuit_round || null,
              circuit_rounds: item.circuitRounds || item.circuit_rounds || null,
              movementId: item.movementId || item.movement?.id,
              set_id: item.setId || item.set_id || null,
            };
          }),
        ],
      };

      if (this.template) {
        action = this.templateUpdate;
        payload.id = this.template.uuid;
      } else {
        action = this.templateStore;
      }

      this.loading = true;
      action(payload).then(() => {
        capturePostHogEvent('template_saved', {
          is_new: !this.template,
          ...this.exerciseSetSummary(),
          movement_count: this.form.fields.items.filter(item => item.type === 'movement').length,
          rest_timer_count: this.form.fields.items.filter(item => item.type === 'rest_timer').length,
        });
        window.app.snackbar({
          type: 'success',
          message: 'Template Saved',
        });
        this.$router.back();
      }).catch((error) => {
        this.form.recordErrors(error);
        window.app.snackbarError(error);
      }).finally(() => {
        this.loading = false;
      });
    },
    loadMovements() {
      this.loading = true;
      return this.movementIndex().finally(() => {
        this.loading = false;
      });
    },
    loadTemplate() {
      this.loading = true;
      this.templateShow({
        id: this.$route.params.templateUUID,
        params: {
          include: [
            'movements',
          ],
        }
      }).then(this.fillForm)
        .catch((error) => {
          window.app.snackbarError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    templateItemsFromTemplate(template) {
      if (template.items?.length) {
        return template.items.map((item) => {
          const movementId = item.movementId || item.movement_id;

          return {
            key: uuid(),
            type: item.type,
            circuitId: item.circuitId || item.circuit_id || null,
            circuitLabel: item.circuitLabel || item.circuit_label || null,
            circuitRound: item.circuitRound || item.circuit_round || null,
            circuitRounds: item.circuitRounds || item.circuit_rounds || null,
            circuitStepId: item.circuitStepId || item.circuit_step_id || null,
            afterCircuitId: item.afterCircuitId || item.after_circuit_id || null,
            movementId,
            movement: movementId ? this.movements.find(movement => movement.id === movementId) : null,
            setId: item.setId || item.set_id || null,
            duration: item.duration || 90,
            label: item.label || null,
          };
        });
      }

      return [
        ...(template.movements || []).map((movement) => ({
          key: uuid(),
          type: 'movement',
          movement,
          movementId: movement.id,
          setId: null,
        })),
      ];
    },
    exerciseSetSummary() {
      const setCounts = Object.values(this.form.fields.items
        .filter(item => item.type === 'movement' && (item.setId || item.set_id))
        .reduce((groups, item) => {
          const setId = item.setId || item.set_id;
          groups[setId] = (groups[setId] || 0) + 1;

          return groups;
        }, {}));
      const multiSetCounts = setCounts.filter(count => count > 1);

      return {
        exercise_sets_enabled: this.exerciseSetsEnabled,
        exercise_set_group_count: setCounts.length,
        exercise_set_max_size: setCounts.length ? Math.max(...setCounts) : 0,
        exercise_set_multi_group_count: multiSetCounts.length,
        exercise_set_total_entries: setCounts.reduce((total, count) => total + count, 0),
        has_exercise_sets: multiSetCounts.length > 0,
      };
    },
    movementItemClasses(index) {
      const position = this.setGroupPosition(index);
      const base = 'overflow-hidden border-neutral-200 bg-white dark:border-neutral-700 dark:bg-white/[2%]';

      if (['single', 'only'].includes(position)) {
        return `${base} my-2 rounded border`;
      }

      if (position === 'first') {
        return `${base} mt-2 rounded-t border-x border-t`;
      }

      if (position === 'last') {
        return `${base} mb-2 rounded-b border-x border-t border-b`;
      }

      return `${base} border-x border-t`;
    },
    movementName(item) {
      return item.movement?.name || 'Movement';
    },
    circuitItems(circuitId) {
      return this.form.fields.items.filter(item => {
        return item.type === 'movement' && item.circuitId === circuitId;
      });
    },
    circuitPreviewItems(circuitId) {
      const firstIndex = this.form.fields.items.findIndex(item => {
        return item.type === 'movement' && item.circuitId === circuitId;
      });
      const secondRoundIndex = this.form.fields.items.findIndex((item, index) => {
        return index > firstIndex
          && item.type === 'movement'
          && item.circuitId === circuitId
          && Number(item.circuitRound) === 2;
      });
      const endIndex = secondRoundIndex >= 0
        ? secondRoundIndex
        : this.form.fields.items.findLastIndex(item => item.type === 'movement' && item.circuitId === circuitId) + 1;

      return this.form.fields.items.slice(firstIndex, endIndex);
    },
    circuitRangeForIndex(index) {
      const circuitIds = [...new Set(this.form.fields.items
        .filter(item => item.type === 'movement' && item.circuitId)
        .map(item => item.circuitId))];

      return circuitIds.find(circuitId => {
        const firstIndex = this.form.fields.items.findIndex(item => {
          return item.circuitId === circuitId;
        });
        const lastIndex = this.form.fields.items.findLastIndex(item => {
          return item.circuitId === circuitId;
        });

        return index >= firstIndex && index <= lastIndex;
      }) || null;
    },
    isCircuitFirstItem(index) {
      const item = this.form.fields.items[index];

      if (item?.type !== 'movement' || !item.circuitId) {
        return false;
      }

      return this.form.fields.items.findIndex(candidate => {
        return candidate.type === 'movement' && candidate.circuitId === item.circuitId;
      }) === index;
    },
    isInsideCircuit(index) {
      return this.circuitRangeForIndex(index) !== null;
    },
    isCircuitStart(index) {
      const item = this.form.fields.items[index];

      if (!this.startsCircuitRound(index) || Number(item.circuitRound) !== 1) {
        return false;
      }

      return this.form.fields.items.findIndex(candidate => {
        return candidate.type === 'movement' && candidate.circuitId === item.circuitId;
      }) === index;
    },
    newCircuitMovement({circuitId, circuitLabel, circuitRound, circuitRounds, movement}) {
      return {
        key: uuid(),
        type: 'movement',
        circuitId,
        circuitLabel,
        circuitRound,
        circuitRounds,
        movement,
        movementId: movement.id,
        setId: null,
      };
    },
    startsCircuitRound(index) {
      const item = this.form.fields.items[index];

      if (item?.type !== 'movement' || !item.circuitId || !item.circuitRound) {
        return false;
      }

      return this.form.fields.items.findIndex(candidate => {
        return candidate.type === 'movement'
          && candidate.circuitId === item.circuitId
          && Number(candidate.circuitRound) === Number(item.circuitRound);
      }) === index;
    },
    setCount(item) {
      const key = this.setGroupKey(item);

      if (!key) {
        return 1;
      }

      return this.form.fields.items.filter(candidate => candidate.type === 'movement' && this.setGroupKey(candidate) === key).length;
    },
    setGroupKey(item) {
      const setId = item.setId || item.set_id;

      return setId ? `set:${setId}` : null;
    },
    setGroupLastItemKey(item) {
      const key = this.setGroupKey(item);

      if (!key) {
        return null;
      }

      const match = [...this.form.fields.items].reverse().find(candidate => {
        return candidate.type === 'movement' && this.setGroupKey(candidate) === key;
      });

      return match?.key || null;
    },
    setGroupPosition(index) {
      const item = this.form.fields.items[index];

      if (!item || item.type !== 'movement') {
        return 'single';
      }

      const key = this.setGroupKey(item);

      if (!key || this.setCount(item) < 2) {
        return 'single';
      }

      const previousItem = this.form.fields.items[index - 1];
      const nextItem = this.form.fields.items[index + 1];
      const hasPreviousSet = previousItem?.type === 'movement' && this.setGroupKey(previousItem) === key;
      const hasNextSet = nextItem?.type === 'movement' && this.setGroupKey(nextItem) === key;

      if (!hasPreviousSet && !hasNextSet) {
        return 'only';
      }

      if (!hasPreviousSet) {
        return 'first';
      }

      if (!hasNextSet) {
        return 'last';
      }

      return 'middle';
    },
    setNumber(item) {
      const key = this.setGroupKey(item);

      if (!key) {
        return 1;
      }

      return this.form.fields.items
        .filter(candidate => candidate.type === 'movement' && this.setGroupKey(candidate) === key)
        .findIndex(candidate => candidate.key === item.key) + 1;
    },
  },
})
</script>

<style scoped lang="scss">

</style>
